import React, { FC, useEffect, Fragment, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    DialogContent,
    Alert,
    Box,
    List,
    ListItem,
    ListItemText,
    Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { Order, OrderStatus } from 'models/order';
import { formatPatientName } from 'utils/format';

type BatchArchiveConfirmDialogProps = {
    selectedOrders: Order[] | undefined;
    handleClose: () => void;
    open: boolean;
    handleConfirm: (filteredOrders: Order[]) => void;
};

const BatchArchiveConfirmDialog: FC<BatchArchiveConfirmDialogProps> = ({
    selectedOrders,
    handleClose,
    open,
    handleConfirm,
}: BatchArchiveConfirmDialogProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [filteredOrders, setFilteredOrders] = useState<Order[]>([] as Order[]);
    const currentUser = currentUserVar();

    useEffect(() => {
        let orders: Order[] | undefined = [];
        if (permissions.isDentist(currentUser.type)) {
            orders = selectedOrders?.filter((order) => order.status.toUpperCase() !== OrderStatus.SENT_BY_CUSTOMER);
            setFilteredOrders(orders as Order[]);
        }
        if (permissions.isLabTechnician(currentUser.type)) {
            orders = selectedOrders?.filter(
                (order) =>
                    order.status.toUpperCase() !== OrderStatus.SENT_BY_CUSTOMER &&
                    order.status.toUpperCase() !== OrderStatus.ACCEPTED_BY_LAB,
            );
            setFilteredOrders(orders as Order[]);
        }
    }, [selectedOrders]);
    return (
        <Fragment>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle id="alert-dialog-title">{translate('orders:BATCH_ARCHIVE_ORDERS')}</DialogTitle>
                {filteredOrders?.length ? (
                    <Fragment>
                        <DialogContent>
                            <Alert severity="info">{translate('orders:BATCH_ARCHIVE_INFO')}</Alert>
                            <List>
                                {filteredOrders.map((order) => {
                                    return (
                                        <Fragment key={order.orderId}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={`${order.orderId} - ${formatPatientName(
                                                        order.patient,
                                                        translate('orders:NO_PATIENT_REFERENCE'),
                                                    )}`}
                                                />
                                            </ListItem>
                                        </Fragment>
                                    );
                                })}
                            </List>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>{translate('common:CANCEL')}</Button>
                            <Button onClick={() => handleConfirm(filteredOrders)} variant="contained" autoFocus>
                                {translate('orders:ARCHIVE_ORDER')}
                            </Button>
                        </DialogActions>
                    </Fragment>
                ) : (
                    <Fragment>
                        <DialogContent>
                            <Box mb={2}>
                                <Alert severity="info">{translate('orders:NO_ARCHIVE_ORDERS')}</Alert>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>{translate('common:CANCEL')}</Button>
                        </DialogActions>
                    </Fragment>
                )}
            </Dialog>
        </Fragment>
    );
};

export default BatchArchiveConfirmDialog;
